import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPhoneAlt } from 'react-icons/fa';
import { MdOutlineMailOutline } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';

// API y utilidades
import PackagesApi from 'frontend/api/paquetes';
import { dataDefaultValues, DATE_KEYS, ROUTE_KEYS, SESSION_STORAGE_KEYS } from 'frontend/constants';
import { useGlobal } from 'frontend/contexts/GlobalContext';
import { useColors } from 'frontend/hooks/useColors';
import useCurrentPath from 'frontend/hooks/useCurrentPath';

// Componentes
import { getCacheStatus } from 'frontend/helpers/getCacheStatus';
import ErrorSnackbar from '../ErrorSnackbar';
import BillboardImage from './components/BillboardImage';
import SearchTabs from './components/SearchTabs';
import { SocialLinks } from './components/SocialLinks';
import NavBarMobile from './NavBarMobile';

// Constantes
const { PACKAGE_ADVICE } = SESSION_STORAGE_KEYS;
const { HOME, BUSCAR_X_PAQUETES } = ROUTE_KEYS;
const routesToRenderFullPackageSearch = [HOME];
const routesToRenderSmallPackageSearch = [BUSCAR_X_PAQUETES];

// Funcion para inicializar los inputs de formulario
const initialPackageSetter = (params) => ({
  origin:
    params?.get('originId') && params?.get('originName')
      ? { id: Number(params.get('originId')), name: params.get('originName').trim() }
      : null,
  destination:
    params?.get('destinationId') && params?.get('destinationName')
      ? { id: Number(params.get('destinationId')), name: params.get('destinationName').trim() }
      : null,
  dateSince: params?.get('dateSince') ? new Date(params.get('dateSince')) : null,
  dateUntil: params?.get('dateUntil') ? new Date(params.get('dateUntil')) : null,
  adultsQty: params?.get('adultsQty') ? Number(params.get('adultsQty')) : 1,
});

const initialFlightSetter = () => ({
  origin: null,
  destination: null,
  passengers: [30],
  departureDate: null,
  returnDate: null,
  direct: false,
  baggage: false,
  cabin: 'Y',
  currency: 'USD',
  isOnlyOneWay: false,
});

export default function MainHeader() {
  const { t } = useTranslation('layout');
  const history = useHistory();
  const { pathname, search } = useLocation();
  const { registerOnSearch } = useGlobal();
  const { background, text } = useColors();
  const { isOnTargetPath } = useCurrentPath([
    ROUTE_KEYS.HOME,
    ROUTE_KEYS.BUSCAR_X_PAQUETES,
    ROUTE_KEYS.BUSCAR_X_VUELOS,
  ]);

  // Estados para datos y formularios
  const [packageOrigins, setPackageOrigins] = useState(dataDefaultValues);
  const [packageDestinations, setPackageDestinations] = useState(dataDefaultValues);
  const [refreshTimestamp, setRefreshTimestamp] = useState(Date.now());
  const [isDestinationEmpty, setIsDestinationEmpty] = useState(false);
  const [isOriginEmpty, setIsOriginEmpty] = useState(false);
  const [isDatesEmpty, setIsDatesEmpty] = useState(false);
  const [isTuristasEmpty, setTuristasEmpty] = useState(false);
  const [originSelected, setOriginSelected] = useState(null);
  const [departureDate, setDepartureDate] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);
  const [isUpdatingFlightInfo, setIsUpdatingFlightInfo] = useState(false);

  const initialPackageFormInput = useMemo(
    () =>
      initialPackageSetter(
        !!search && pathname == '/buscarxpaquetes' ? new URLSearchParams(search) : undefined,
      ),
    [search],
  );

  const initialFlightFormInput = useMemo(() => initialFlightSetter(), []);

  const [packageFormInput, setPackageFormInput] = useState(initialPackageFormInput);
  const [flightFormInput, setFlightFormInput] = useState(initialFlightFormInput);

  // Datos del template
  const image = useSelector((state) => state.applicationState.templateImage);
  const templateDate = useSelector((state) => state.applicationState.templateData);
  const header = useSelector((state) => state.applicationState.templateData.header);
  const enableSearchPackage = templateDate?.headerPaquete?.[0]?.esFooter || false;
  const enableSearchFlights = templateDate?.headerVuelos?.[0]?.esFooter || false;

  // Manejadores para formulario de paquetes
  const handlePackageOrigin = (origin) => {
    setPackageFormInput((state) => ({
      ...state,
      origin,
      destination: null,
    }));
    setOriginSelected(origin);
  };

  const handlePackageDestination = (destination) => {
    setPackageFormInput((state) => ({ ...state, destination }));
    if (originSelected?.id && destination?.id) {
      fetchAvailableDates({ originId: originSelected.id, destinationId: destination.id });
    }
  };

  const handleOccupationChange = (adultsQty) => {
    setPackageFormInput((state) => ({ ...state, adultsQty }));
  };

  const handleDateRangeSelect = (startDate, endDate) => {
    setPackageFormInput({ ...packageFormInput, dateSince: startDate, dateUntil: endDate });
  };

  // Manejadores para formulario de vuelos
  const updateFlightInfo = useCallback(async (origin, destination, currentFlightFormInput) => {
    if (!origin || !destination || isUpdatingFlightInfo) return;

    try {
      setIsUpdatingFlightInfo(true);
      const [originData, destinationData] = await Promise.all([
        PackagesApi.getAirportById(origin.id),
        PackagesApi.getAirportById(destination.id)
      ]);

      if (!originData || !destinationData) {
        throw new Error('No se pudieron obtener los datos de los aeropuertos');
      }

      const isInternational = originData.countryCode !== destinationData.countryCode;
      const flightInfo = {
        originCountryCode: originData.countryCode,
        destinationCountryCode: destinationData.countryCode,
        origin: {
          id: originData.id,
          airportCode: originData.airportCode,
          airportName: originData.airportName,
          cityCode: originData.cityCode,
          cityName: originData.cityName,
          countryCode: originData.countryCode
        },
        destination: {
          id: destinationData.id,
          airportCode: destinationData.airportCode,
          airportName: destinationData.airportName,
          cityCode: destinationData.cityCode,
          cityName: destinationData.cityName,
          countryCode: destinationData.countryCode
        },
        departureDate: currentFlightFormInput.departureDate ? new Date(currentFlightFormInput.departureDate) : null,
        returnDate: currentFlightFormInput.returnDate ? new Date(currentFlightFormInput.returnDate) : null,
        passengers: currentFlightFormInput.passengers,
        isOnlyOneWay: currentFlightFormInput.isOnlyOneWay,
        isInternational
      };

      sessionStorage.setItem('flightInfo', JSON.stringify(flightInfo));
      return flightInfo;
    } catch (error) {
      console.error('🔍 [MainHeader] updateFlightInfo - Error:', error);
      throw error;
    } finally {
      setIsUpdatingFlightInfo(false);
    }
  }, [isUpdatingFlightInfo]);

  const handleFlightOrigin = useCallback(async (origin) => {
    if (!origin) return;

    try {
      setFlightFormInput((state) => {
        const newState = { ...state, origin };
        if (state.destination) {
          updateFlightInfo(origin, state.destination, newState)
            .catch(error => console.error('Error updating flight info:', error));
        }
        return newState;
      });
    } catch (error) {
      console.error('🔍 [MainHeader] handleFlightOrigin - Error:', error);
    }
  }, [updateFlightInfo]);

  const handleFlightDestination = useCallback(async (destination) => {
    if (!destination) return;

    try {
      setFlightFormInput((state) => {
        const newState = { ...state, destination };
        if (state.origin) {
          updateFlightInfo(state.origin, destination, newState)
            .catch(error => console.error('Error updating flight info:', error));
        }
        return newState;
      });
    } catch (error) {
      console.error('🔍 [MainHeader] handleFlightDestination - Error:', error);
    }
  }, [updateFlightInfo]);

  const handleFlightPassenger = (passengers) => {
    setFlightFormInput((state) => ({ ...state, passengers }));
  };

  const handleFlightsDateRangeSelect = (departureDate, returnDate) => {
    setFlightFormInput({ ...flightFormInput, departureDate, returnDate });
  };

  const handleIsOnlyOneWay = (isOnlyOneWay) => {
    setFlightFormInput({ ...flightFormInput, isOnlyOneWay });
  };

  // Manejadores para errores y datos
  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const getPackageOrigins = () => {
    setPackageOrigins((state) => ({ ...state, isLoading: true }));
  };

  const getPackageDestinations = () => {
    setPackageDestinations((state) => ({ ...state, isLoading: true }));
  };

  // Funciones para obtener datos
  const fetchOrigins = useCallback(async () => {
    try {
      const originsResponse = await PackagesApi.getOrigins();
      window.sessionStorage.removeItem(PACKAGE_ADVICE);
      setPackageOrigins({ isLoading: false, error: false, data: originsResponse });
    } catch (error) {
      showError(error?.message);
      if (!window.sessionStorage.getItem(PACKAGE_ADVICE)) {
        window.sessionStorage.setItem(PACKAGE_ADVICE, true);
      }
      setPackageOrigins({ data: [], error: true, isLoading: false });
    }
  }, []);

  const fetchDestinations = useCallback(async () => {
    try {
      const originId =
        packageFormInput?.origin?.id?.join(',') ?? new URLSearchParams(search).get('destinationId');
      const destinationsResponse = await PackagesApi.getDestinations(originId);
      setPackageDestinations({
        isLoading: false,
        error: false,
        data: destinationsResponse,
      });
    } catch (error) {
      showError(error?.message);
      setPackageDestinations({ data: [], error: true, isLoading: false });
    }
  }, [packageFormInput]);

  const fetchAvailableDates = async (query) => {
    try {
      const response = await PackagesApi.getAvailableDates(query);
      const utcDate = moment.utc(response.tripInformation_departure_date).toISOString();
      setDepartureDate(utcDate);
    } catch (error) {
      console.error('Error fetching available dates:', error);
    }
  };

  // Funciones para construir query strings
  const getQueryString = useCallback(() => {
    if (!packageFormInput?.dateSince) return '';

    const { origin, destination, dateSince, dateUntil, adultsQty } = packageFormInput;

    const params = new URLSearchParams();

    params.set('dateSince', moment(dateSince).format(DATE_KEYS.FORMAT.ISO));
    if (dateUntil) params.set('dateUntil', moment(dateUntil).format(DATE_KEYS.FORMAT.ISO));

    if (origin) {
      const originIds = Array.isArray(origin.id) ? origin.id.join(',') : String(origin.id);
      params.set('originId', originIds);
      params.set('originName', origin.name);
    }

    if (destination) {
      const destinationIds = Array.isArray(destination.id)
        ? destination.id.join(',')
        : String(destination.id);
      params.set('destinationId', destinationIds);
      params.set('destinationName', destination.name);
    }

    if (adultsQty) params.set('adultsQty', adultsQty);

    params.set('cache', getCacheStatus());
    params.set('_refresh', refreshTimestamp);

    return `?${params.toString()}`;
  }, [packageFormInput, refreshTimestamp, getCacheStatus]);

  const getFlightsQueryString = useCallback(() => {
    const { origin, destination, departureDate, returnDate, passengers, isOnlyOneWay } =
      flightFormInput;
    const flightParams = new URLSearchParams();

    flightParams.set('departureDate', moment(departureDate).format(DATE_KEYS.FORMAT.ISO));

    if (returnDate) flightParams.set('returnDate', moment(returnDate).format(DATE_KEYS.FORMAT.ISO));

    if (origin) {
      flightParams.set('originId', origin.cityCode);
      flightParams.set('originName', origin.cityName);
      flightParams.set('originIdn', origin.id);
    }

    if (destination) {
      flightParams.set('destinationId', destination.cityCode);
      flightParams.set('destinationName', destination.cityName);
      flightParams.set('destinationIdn', destination.id);
    }

    if (passengers) flightParams.set('passengers', passengers.join(','));

    if (isOnlyOneWay) flightParams.set('isOnlyOneWay', true);

    flightParams.set('cache', getCacheStatus());
    flightParams.set('_refresh', refreshTimestamp);

    return `?${flightParams.toString()}`;
  }, [flightFormInput, refreshTimestamp]);

  // Propiedades de navegación
  const linkTo = useMemo(
    () => ({
      pathname: ROUTE_KEYS.BUSCAR_X_PAQUETES,
      search: getQueryString(),
    }),
    [getQueryString],
  );

  const linkToFLights = useMemo(
    () =>
      flightFormInput?.destination && flightFormInput?.origin
        ? {
            pathname: ROUTE_KEYS.BUSCAR_X_VUELOS,
          }
        : '#',
    [flightFormInput],
  );

  // Manejadores de búsqueda
  const handleSearchButton = useCallback(
    (e) => {
      e.preventDefault();
      registerOnSearch();

      // Validate required fields
      if (!packageFormInput.origin) {
        setIsOriginEmpty(true);
        showError('Por favor seleccione un origen');
        return;
      }
      if (!packageFormInput.destination) {
        setIsDestinationEmpty(true);
        showError('Por favor seleccione un destino');
        return;
      }
      if (!packageFormInput.dateSince || !packageFormInput.dateUntil) {
        setIsDatesEmpty(true);
        showError('Por favor seleccione las fechas');
        return;
      }
      if (!packageFormInput.adultsQty || packageFormInput.adultsQty < 1) {
        setTuristasEmpty(true);
        showError('Por favor seleccione la cantidad de pasajeros');
        return;
      }

      try {
        setRefreshTimestamp(Date.now());
        history.push(linkTo.pathname + getQueryString());
      } catch (error) {
        showError(error?.message);
      }
    },
    [linkTo, packageFormInput, getQueryString, history, registerOnSearch, showError],
  );

  const handleSearchFlightsButton = useCallback(async (e) => {
    e.preventDefault();
    registerOnSearch();

    // Validate required fields
    if (!flightFormInput.origin) {
      showError('Por favor seleccione un origen');
      return;
    }
    if (!flightFormInput.destination) {
      showError('Por favor seleccione un destino');
      return;
    }
    if (!flightFormInput.departureDate) {
      showError('Por favor seleccione una fecha de salida');
      return;
    }
    if (!flightFormInput.isOnlyOneWay && !flightFormInput.returnDate) {
      showError('Por favor seleccione una fecha de regreso');
      return;
    }
    if (!flightFormInput.passengers || flightFormInput.passengers.length === 0) {
      setFlightFormInput((prev) => ({ ...prev, passengers: [30] }));
    }

    try {
      await updateFlightInfo(flightFormInput.origin, flightFormInput.destination, flightFormInput);
      setRefreshTimestamp(Date.now());
      history.push(linkToFLights.pathname + getFlightsQueryString());
    } catch (error) {
      console.error('🔍 [MainHeader] handleSearchFlightsButton - Error:', error);
      showError(error?.message || 'Error al procesar la búsqueda');
    }
  }, [linkToFLights, getFlightsQueryString, history, registerOnSearch, flightFormInput, showError, updateFlightInfo]);

  // Inicialización de datos de vuelos desde URL
  useEffect(() => {
    const fetchInitialFlightInput = async () => {
      if (!!search && pathname === '/buscarxvuelos') {
        const params = new URLSearchParams(search);

        const originData = await PackagesApi.getAirportById(params?.get('originIdn'));
        const destinationData = await PackagesApi.getAirportById(params?.get('destinationIdn'));
        const depDate = params?.get('departureDate') ? moment(params.get('departureDate')) : null;
        const retDate = params?.get('returnDate') ? moment(params.get('returnDate')) : null;
        const isOnlyOneWay = params?.get('isOnlyOneWay');
        const cache = params?.get('cache');

        const initialFlightData = {
          origin: originData || null,
          destination: destinationData || null,
          passengers: [],
          departureDate: depDate,
          returnDate: retDate,
          direct: false,
          baggage: false,
          cabin: 'Y',
          currency: 'USD',
          isOnlyOneWay: isOnlyOneWay || null,
          cache,
        };

        setFlightFormInput(initialFlightData);
      }
    };

    fetchInitialFlightInput();
  }, [search, pathname]);

  // Carga de orígenes
  useEffect(() => {
    const shouldFetchOrigins = [
      ...routesToRenderFullPackageSearch,
      ...routesToRenderSmallPackageSearch,
    ].includes(pathname);

    if (shouldFetchOrigins) {
      getPackageOrigins();
    }
  }, [pathname]);

  useEffect(() => {
    if (packageOrigins?.isLoading) {
      fetchOrigins();
    } else if (
      routesToRenderSmallPackageSearch.includes(pathname) &&
      packageFormInput?.origin?.id &&
      packageOrigins?.data?.length
    ) {
      const origin = packageOrigins.data.find(({ id }) =>
        Array.isArray(packageFormInput.origin.id)
          ? packageFormInput.origin.id.includes(id)
          : packageFormInput.origin.id === id,
      );
      if (!origin) history.push(ROUTE_KEYS.HOME);
    }
  }, [packageOrigins, pathname, packageFormInput.origin?.id, history, fetchOrigins]);

  // Carga de destinos cuando cambia el origen
  useEffect(() => {
    const originId = packageFormInput.origin?.id;
    if (originId) {
      getPackageDestinations();
    } else {
      setPackageDestinations(dataDefaultValues);
    }
  }, [packageFormInput.origin?.id]);

  useEffect(() => {
    if (packageDestinations?.isLoading) {
      fetchDestinations();
    } else if (
      routesToRenderSmallPackageSearch.includes(pathname) &&
      packageFormInput?.destination?.id &&
      packageDestinations?.data?.length
    ) {
      const destination = packageDestinations.data.find(({ id }) =>
        Array.isArray(packageFormInput.destination.id)
          ? packageFormInput.destination.id.includes(id)
          : packageFormInput.destination.id === id,
      );
      if (!destination) history.push(ROUTE_KEYS.HOME);
    }
  }, [packageDestinations, pathname, packageFormInput.destination?.id, history, fetchDestinations]);

  useEffect(() => {
    if (ROUTE_KEYS.HOME === pathname) {
      sessionStorage.clear();
    }
  }, [pathname]);

  return (
    <div className="header__container">
      <BillboardImage
        image={image}
        pathname={pathname}
        routesToRenderFullPackageSearch={routesToRenderFullPackageSearch}
      />
      <div className="header__topbar justify-between">
        <div>
          <a href="/">
            <img className="w-32 md:w-80" src={header.logo} alt="Logo" />
          </a>
        </div>
        <div className="flex gap-9 items-center">
          <div className="media__icons__container">
            {header.emailContacto && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <MdOutlineMailOutline style={{ fontSize: '20px' }} />
                <p
                  className="contact__icon"
                  style={{
                    textTransform: 'lowercase',
                  }}
                >
                  {header.emailContacto}
                </p>
              </div>
            )}
            {header.telefonoContacto && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <FaPhoneAlt />
                <h2 className="contact__icon">{header.telefonoContacto}</h2>
              </div>
            )}
            <SocialLinks socialNetworks={templateDate?.socialNetworks} />
          </div>
          <NavBarMobile />
        </div>
      </div>
      <div className="stacked__container">
        <SearchTabs
          enableSearchPackage={enableSearchPackage}
          enableSearchFlights={enableSearchFlights}
          isOnTargetPath={isOnTargetPath}
          packageFormInput={packageFormInput}
          packageDestinations={packageDestinations}
          packageOrigins={packageOrigins}
          handlePackageOrigin={handlePackageOrigin}
          handlePackageDestination={handlePackageDestination}
          handleDateRangeSelect={handleDateRangeSelect}
          flightFormInput={flightFormInput}
          handleFlightOrigin={handleFlightOrigin}
          handleFlightDestination={handleFlightDestination}
          handleFlightsDateRangeSelect={handleFlightsDateRangeSelect}
          handleFlightPassenger={handleFlightPassenger}
          handleOccupationChange={handleOccupationChange}
          handleIsOnlyOneWay={handleIsOnlyOneWay}
          isDestinationEmpty={isDestinationEmpty}
          isOriginEmpty={isOriginEmpty}
          isDatesEmpty={isDatesEmpty}
          isTuristasEmpty={isTuristasEmpty}
          setIsOriginEmpty={setIsOriginEmpty}
          setIsDatesEmpty={setIsDatesEmpty}
          setTuristasEmpty={setTuristasEmpty}
          setIsDestinationEmpty={setIsDestinationEmpty}
          departureDate={departureDate}
          handleSearchButton={handleSearchButton}
          handleSearchFlightsButton={handleSearchFlightsButton}
          linkTo={linkTo}
          linkToFLights={linkToFLights}
          background={background}
          text={text}
        />
      </div>

      <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
    </div>
  );
}
